// src/pages/CompleteRegistration.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "../styles/CompleteRegistration.css";
import ResendRegistrationLinkBanner from "../components/ResendRegistrationLinkBanner";
import api from "../services/api";

const CompleteRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);

  // Extract token from URL and try to parse email for helpful UI
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token") || "";
    setToken(t);

    if (t) {
      try {
        const decoded = jwtDecode(t);
        if (decoded?.email) setEmail(decoded.email);
      } catch {
        // token may be expired/invalid; backend will perform the real validation
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Missing registration token.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setSubmitting(true);

    try {
      // ✅ Send JSON body with the exact keys FastAPI expects
      const { data } = await api.post("/auth/complete-registration", {
        token,
        new_password: password,
      });

      // Expect: { access_token, refresh_token, token_type }
      const access = data?.access_token;
      const refresh = data?.refresh_token;

      if (!access) {
        throw new Error("Registration succeeded but no token was returned.");
      }

      localStorage.setItem("token", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);

      // Route by role
      const decoded = jwtDecode(access);
      const role = decoded?.role;
      if (role === "ADMIN") navigate("/admin", { replace: true });
      else if (role === "PRINCIPAL")
        navigate("/principal-dashboard", { replace: true });
      else if (role === "STAFF") navigate("/staff-dashboard", { replace: true });
      else navigate("/view-photos", { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(detail);

      if (String(detail).toLowerCase().includes("expired")) {
        setShowResend(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="complete-registration-page">
      <div className="complete-registration-container">
        <h2 className="form-title">Complete your GhrianGhraffy setup</h2>
        <p className="complete-registration-subtitle">
          Choose a secure password to finish activating your account.
        </p>

        {error && (
          <p className="error-message" aria-live="polite">
            {error}
          </p>
        )}

        {showResend && email && (
          <div className="resend-banner-wrapper">
            <ResendRegistrationLinkBanner email={email} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-group">
            <label className="form-label">Your email</label>
            <input
              type="email"
              className="form-input form-input--readonly"
              value={email || ""}
              readOnly
            />
          </div>

          <div className="form-group">
            <label className="form-label">Create a new password</label>
            <input
              type="password"
              className="form-input green-placeholder"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <small className="field-hint">
              Tip: avoid re-using your email password. A mix of words, numbers
              and symbols works well.
            </small>
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Completing…" : "Click to complete setup"}
          </button>
        </form>

        <p className="complete-registration-footer">
          Already finished? <Link to="/login">Go to Login</Link>
        </p>
      </div>
    </div>
  );
};

export default CompleteRegistration;
